import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  Typography,
  Box,
  Divider,
  Grid,
  TextField,
  Chip,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import * as Icons from "@material-ui/icons";
// Contexts
import NotificationContext from "views/Components/Context.js";
import TaskDefinitionModal from "views/Components/TaskDefinitionModal";
import streamlitTemplates from "views/Dashboard/Data/streamlitTemplates";
import domainsData from "assets/data/domainsAndPersonas.json";

// Styles
import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";

// Custom Components
import { fetchLabAPI, addResourceToLabAPI } from "views/APIs/APIUtility";
import WritingTable from "../Writing/WritingTable";
import FileUploadDialog from "../FileUploadDialog";
import { _mapResourceToAppType } from "../CommonFunctions";
import { deleteLabResourceAPI, updateLabStatus } from "views/APIs/APIUtility";
import Button from "components/CustomButtons/Button";
import Alert from "views/Components/Alert";
import ScrapperModal from "./ScrapperModal";
import {
  generateIdeasForConceptLabRefactored,
  generateRequirementsForConceptLabRefactored,
} from "../../APIs/LabsUtility";

const useStyles = makeStyles((theme) => ({
  ...styles,
}));

export default function UploadSourceFile({ labID, setLabName, setActiveStep }) {
  const classes = useStyles();
  const { showNotification } = React.useContext(NotificationContext);

  const [openScrapperModal, setOpenScrapperModal] = useState(false);
  const [resourceAddModal, setResourceAddModal] = useState(false);
  const [lab, setLab] = useState(null);
  const [files, setFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [loading, setLoading] = useState({ isLoading: false, message: "" });
  // Instructions + Templates (replacing Lab Name & Description UI)
  const [taskDefinition, setTaskDefinition] = useState("");
  const [openTemplateModal, setOpenTemplateModal] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedPersonas, setSelectedPersonas] = useState([]);
  const [customPersona, setCustomPersona] = useState("");
  const [newResource, setNewResource] = useState({
    name: "",
    description: "",
    file: null,
  });
  const [showAlert, setShowAlert] = useState(false);

  // Just required but not getting used
  const [selectedResources, setSelectedResources] = useState([]);
  const [replace, setReplace] = useState(false);

  useEffect(() => {
    async function fetchLabDetails() {
      try {
        setLoading({ isLoading: true, message: "Fetching lab details..." });
        const data = await fetchLabAPI(labID);
        setLab(data);
        setTaskDefinition(data.task_definition || "");
        // Try to restore selected domain from personas
        if (data.selected_domain) {
          const domain = domainsData.domains.find(
            (d) => d.id === data.selected_domain
          );
          setSelectedDomain(domain || null);
          
          // Convert persona name strings back to persona objects
          if (data.selected_personas && Array.isArray(data.selected_personas) && domain) {
            const personaObjects = [];
            let customPersonaValue = "";
            
            data.selected_personas.forEach(personaName => {
              // Find the persona object in the domain's personas
              const persona = domain.personas.find(p => p.name === personaName);
              if (persona) {
                personaObjects.push(persona);
              } else {
                // If persona not found in domain list, it's a custom persona
                customPersonaValue = personaName;
                // Add the "Custom" option to selected personas
                const customOption = domain.personas.find(p => p.name === "Custom");
                if (customOption && !personaObjects.find(p => p.name === "Custom")) {
                  personaObjects.push(customOption);
                }
              }
            });
            
            setSelectedPersonas(personaObjects);
            setCustomPersona(customPersonaValue);
          } else {
            setSelectedPersonas([]);
            setCustomPersona("");
          }
        } else {
          setSelectedPersonas(data.selected_personas || []);
          setCustomPersona("");
        }
        handleSetFileResources(data.raw_resources);
        setLoading({ isLoading: false, message: "" }); // Ensure loading state is reset
      } catch (error) {
        showNotification("Error fetching lab details", "danger");
      } finally {
      }
    }

    fetchLabDetails();
  }, [labID]);

  const handleSetFileResources = (resources = []) => {
    if (!Array.isArray(resources)) return;
    const mappedResources = resources.map(_mapResourceToAppType);
    setFiles(mappedResources);
  };

  // Update lab details removed from UI (placeholder retained if needed later)
  const handleUpdateLabDetails = async () => {};

  const handleDeleteResource = async (resource, index) => {
    try {
      const formData = new FormData();
      formData.append("lab_id", labID);
      formData.append("resource_id", resource.id);
      await deleteLabResourceAPI(formData);
      const updatedResources = files.filter((item, idx) => idx !== index);
      setFiles(updatedResources);
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  const handleReplace = async (resourceID, file, file_type) => {};

  const handleScrapperModalClose = () => {
    setOpenScrapperModal(false);
  };

  const handleDomainChange = (event, newValue) => {
    setSelectedDomain(newValue);
    setSelectedPersonas([]); // Clear personas when domain changes
    setCustomPersona(""); // Clear custom persona input
  };

  const handlePersonasChange = (event, newValue) => {
    setSelectedPersonas(newValue);
    // Clear custom persona if Custom is deselected
    const hasCustom = newValue.some((p) => p.name === "Custom");
    if (!hasCustom) {
      setCustomPersona("");
    }
  };

  const getAvailablePersonas = () => {
    if (!selectedDomain) return [];
    return selectedDomain.personas;
  };

  // Direct file upload trigger (OptionsModal removed)
  const handleOpenFileUpload = () => setResourceAddModal(true);

  const handleFileUploadDialogClose = () => {
    setResourceAddModal(false);
    setNewResource({ name: "", description: "", file: null });
  };
  const handleReplaceResource = async () => {};

  const handleAddResource = async () => {
    try {
      const filesToUpload = Array.isArray(newResource.files)
        ? newResource.files
        : newResource.file
        ? [newResource.file]
        : [];
      if (!filesToUpload.length) {
        showNotification("Please select at least one file", "danger");
        return;
      }
      setResourceAddModal(false);
      setLoading({ isLoading: true, message: "Adding Files..." });
      let lastResponse = null;
      for (const f of filesToUpload) {
        const formData = new FormData();
        formData.append("lab_id", labID);
        let fileType = f.type;
        if (!fileType || fileType === "") {
          const extension = f.name.split(".").pop()?.toLowerCase();
          if (extension === "md") fileType = "text/markdown";
        }
        if (fileType?.startsWith("image/")) {
          formData.append("resource_type", "Image");
        } else if (fileType === "application/pdf") {
          formData.append("resource_type", "File");
        } else if (fileType === "text/csv") {
          formData.append("resource_type", "Dataset");
        } else if (fileType === "text/markdown") {
          formData.append("resource_type", "text/markdown");
        } else {
          formData.append("resource_type", fileType || "File");
        }
        formData.append("resource_name", f.name);
        formData.append("resource_description", `${f.name} file uploaded`);
        formData.append("resource_file", f);
        try {
          lastResponse = await addResourceToLabAPI(formData);
        } catch (innerErr) {
          showNotification(
            `Failed to add ${f.name}: ${innerErr.message || innerErr}`,
            "danger"
          );
        }
      }
      if (lastResponse?.raw_resources) {
        handleSetFileResources(lastResponse.raw_resources);
      }
      showNotification("Files processed", "success");
      setNewResource({ name: "", description: "", file: null, files: [] });
    } catch (error) {
      showNotification(`Upload error: ${error.message || "Unknown"}`, "danger");
    } finally {
      handleFileUploadDialogClose();
      setLoading({ isLoading: false, message: "" });
    }
  };

  const handleUploadSourceFile = async () => {
    if (files.length === 0) {
      showNotification(
        "Please upload at least one file to get started",
        "danger"
      );
      return;
    }
    if (lab.status !== "In Design Phase") {
      setShowAlert(true);
    } else {
      await submitHandler();
    }
  };

  const submitHandler = async () => {
    try {
      setLoading({ isLoading: true, message: "Generating Lab Ideas..." });
      const formData = new FormData();
      formData.append("lab_id", labID);
      formData.append("task_definition", taskDefinition);
      // Extract persona names, replace Custom with actual custom input
      const personaNames = selectedPersonas.map((p) =>
        p.name === "Custom" ? customPersona.trim() || "Custom" : p.name
      );
      formData.append("selected_personas", JSON.stringify(personaNames));
      formData.append("selected_domain", selectedDomain?.id || "");
      // formData.append("lab_design_status", "Lab Design");
      // await updateLabStatus(formData);
      await generateIdeasForConceptLabRefactored(formData);
      window.location.reload();
    } catch (error) {
      showNotification("Error generating design", "danger");
    } finally {
      setLoading({ isLoading: false, message: "" });
    }
  };

  const handleConfirm = () => {
    setShowAlert(false); // Close the alert
    submitHandler();
  };

  const handleCancel = () => {
    setShowAlert(false); // Close the alert
  };

  return (
    <>
      {showAlert && (
        <Alert
          title="Are you sure?"
          warning={true}
          showCancel={true}
          cancelBtnText="No"
          confirmBtnText="Yes"
          onCancel={handleCancel}
          onConfirm={handleConfirm}
        >
          Proceeding to the next step will reset your current progress in
          further steps. Your existing work will be overwritten, and new content
          will be created. Are you sure you want to continue?
        </Alert>
      )}
      {/* Pass props to FileUploadDialog */}
      <FileUploadDialog
        open={resourceAddModal}
        onClose={handleFileUploadDialogClose}
        newFile={newResource}
        setNewFile={setNewResource}
        onSave={replace ? handleReplaceResource : handleAddResource}
        replace={replace}
        file_type="multifile"
      />

      <ScrapperModal
        open={openScrapperModal}
        onClose={handleScrapperModalClose}
        labID={labID}
        handleSetFileResources={handleSetFileResources}
      />

      {loading.isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress className={classes.loaderClass} />
          <Typography
            variant="h6"
            style={{ marginLeft: 16, textTransform: "none" }}
          >
            {loading.message}
          </Typography>
        </Box>
      ) : (
        lab && (
          <>
            <Divider  />
            <Grid container>
              {/* Domain Selection */}
              <Grid
                item
                xs={12}
                md={6}
                style={{ marginTop: 10, paddingRight: 12 }}
              >
                <Typography
                  variant="h6"
                  style={{ textTransform: "none", marginBottom: 8 }}
                >
                  Select Domain
                </Typography>
                <Autocomplete
                  value={selectedDomain}
                  onChange={handleDomainChange}
                  options={domainsData.domains}
                  getOptionLabel={(option) => option.name}
                  renderOption={(option) => {
                    const IconComponent = Icons[option.icon] || Icons.Work;
                    return (
                      <Box display="flex" alignItems="center">
                        <IconComponent
                          style={{ marginRight: 12, color: "#20677b" }}
                        />
                        <Typography>{option.name}</Typography>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select a domain"
                      InputProps={{
                        ...params.InputProps,
                        style: { fontSize: "0.9rem", backgroundColor: "#fff" },
                        startAdornment: selectedDomain ? (
                          <Box
                            display="flex"
                            alignItems="center"
                            style={{ marginLeft: 8 }}
                          >
                            {React.createElement(
                              Icons[selectedDomain.icon] || Icons.Work,
                              {
                                style: { color: "#20677b", marginRight: 4 },
                              }
                            )}
                          </Box>
                        ) : null,
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Personas Selection */}
              <Grid
                item
                xs={12}
                md={6}
                style={{ marginTop: 10, paddingLeft: 12 }}
              >
                <Typography
                  variant="h6"
                  style={{ textTransform: "none", marginBottom: 8 }}
                >
                  Select Personas (Who is the intended user?)
                </Typography>
                <Autocomplete
                  multiple
                  value={selectedPersonas}
                  onChange={handlePersonasChange}
                  options={getAvailablePersonas()}
                  disabled={!selectedDomain}
                  getOptionLabel={(option) => option.name}
                  renderOption={(option) => {
                    const IconComponent = Icons[option.icon] || Icons.Person;
                    return (
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        style={{ padding: "4px 0" }}
                      >
                        <IconComponent
                          style={{
                            marginRight: 12,
                            marginTop: 4,
                            color: "#20677b",
                          }}
                        />
                        <Box>
                          <Typography
                            variant="body1"
                            style={{ fontWeight: 500 }}
                          >
                            {option.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {option.description}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        icon={React.createElement(
                          Icons[option.icon] || Icons.Person,
                          {
                            style: { color: "white" },
                          }
                        )}
                        label={option.name}
                        {...getTagProps({ index })}
                        style={{
                          backgroundColor: "#20677b",
                          color: "white",
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={
                        selectedDomain
                          ? "Select personas"
                          : "Select a domain first"
                      }
                      InputProps={{
                        ...params.InputProps,
                        style: { fontSize: "0.9rem", backgroundColor: "#fff" },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Custom Persona Input - Only show when Custom is selected */}
              {selectedPersonas.some((p) => p.name === "Custom") && (
                <Grid item xs={12} style={{ marginTop: 10 }}>
                  <TextField
                    value={customPersona}
                    onChange={(e) => setCustomPersona(e.target.value)}
                    fullWidth
                    variant="outlined"
                    placeholder="Enter custom persona name"
                    label="Custom Persona"
                    InputProps={{
                      style: { fontSize: "0.9rem", backgroundColor: "#fff" },
                    }}
                  />
                </Grid>
              )}
              <Grid
                item
                xs={12}
                style={{ marginTop: "20px",}}
              >
                <Typography
                  variant="h6"
                  style={{ textTransform: "none", marginBottom: 14 }}
                >
                  Specify Task Definition / Instructions
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  value={taskDefinition}
                  onChange={(e) => setTaskDefinition(e.target.value)}
                  fullWidth
                  multiline
                  rows={6}
                  variant="outlined"
                  placeholder={`Examples:
- "Develop a dashboard for portfolio managers to analyze asset allocation and performance metrics. Use the provided resources to note the key metrics."
- "Build a risk management tool that assesses exposure and simulates stress test scenarios. Incorporate the given resources to highlight key risk factors."
- "Design a quant research module for factor testing and backtest summaries with visualizations. Leverage the resources to showcase factor performance and diagnostics."
`}
                  InputProps={{
                    style: {
                      fontSize: "0.9rem",
                      lineHeight: "1.5",
                      backgroundColor: "#fff",
                    },
                  }}
                  InputLabelProps={{ style: { fontSize: "0.9rem" } }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                style={{ marginTop: "20px", marginBottom: "10px" }}
              >
                <Typography
                  variant="h6"
                  style={{ textTransform: "none", marginBottom: 3 }}
                >
                  Upload Supporting File(s)
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <WritingTable
                  files={files}
                  handleDeleteResource={handleDeleteResource}
                  handleReplace={handleReplace}
                  handleResourceAddition={handleOpenFileUpload}
                  setSelectedResources={setNewFiles}
                  selectedResources={selectedResources}
                  selectColumnRequired={false}
                />
              </Grid>
            </Grid>
            <Grid container style={{ marginTop: "10px" }}>
              <Grid item xs={12}>
                <Button
                  color="quCreateColor"
                  style={{ float: "right", fontWeight: "bold" }}
                  onClick={handleUploadSourceFile}
                  endIcon={<NavigateNextIcon />}
                  disabled={
                    !taskDefinition.trim() ||
                    !selectedDomain ||
                    selectedPersonas.length === 0 ||
                    (selectedPersonas.some((p) => p.name === "Custom") &&
                      !customPersona.trim())
                  }
                >
                  Next
                </Button>
                {/* Save Lab Details button removed */}
              </Grid>
            </Grid>
          </>
        )
      )}
      <TaskDefinitionModal
        open={openTemplateModal}
        onClose={() => setOpenTemplateModal(false)}
        setTaskDefinition={setTaskDefinition}
        module_name={lab?.lab_name}
        module_description={lab?.lab_description}
        templatesOverride={streamlitTemplates}
        selectedPersonas={selectedPersonas}
        setSelectedPersonas={setSelectedPersonas}
        multiSelect={true}
      />
    </>
  );
}
